import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL, getAuthConfig } from "./shared";

/*
 * Accueil & file d'attente (cahier des charges labo, §12-14). Port de
 * ReceptionModule (frontend/src/pages/HopitalDashboard.js) sur les routes
 * dédiées /api/lab/reception/*, /api/lab/patients et /api/lab/act-types
 * (backend/routes/lab/reception.py PR3b, backend/routes/lab/act_types.py
 * PR6c) : regroupe en un seul onglet la recherche/arrivée du patient
 * (Accueil) et la file d'attente temps réel.
 */
const RECEPTION_STATUS_LABELS = { attente: "En attente", appelee: "Appelée", en_cours: "Prise en charge" };
const RECEPTION_STATUS_COLORS = {
  attente: ["#FEF3C7", "#92400E"],
  appelee: ["#DBEAFE", "#1E40AF"],
  en_cours: ["#DCFCE7", "#166534"],
};

export default function ReceptionModule({ can }) {
  const [queue, setQueue] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [arriving, setArriving] = useState(null);
  const [actTypes, setActTypes] = useState([]);
  const [actType, setActType] = useState("");
  const [hasApt, setHasApt] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const loadQueue = async () => {
    try { const r = await axios.get(`${API_URL}/api/lab/reception/queue`, getAuthConfig()); setQueue(r.data.queue || []); }
    catch (e) { /* silencieux : le rafraichissement periodique reessaiera */ }
  };

  useEffect(() => {
    loadQueue();
    const poll = setInterval(loadQueue, 8000);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    axios.get(`${API_URL}/api/lab/act-types`, getAuthConfig()).then((r) => setActTypes(r.data.act_types || [])).catch(() => {});
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  const doSearch = async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API_URL}/api/lab/patients`, { ...getAuthConfig(), params: { search: q, limit: 8 } });
      setResults(r.data.patients || []);
    } catch (e) { setResults([]); }
    finally { setSearching(false); }
  };

  const openArrival = (p) => { setArriving(p); setActType(""); setHasApt(false); setError(""); };

  const confirmArrival = async () => {
    // Correctif L-DOUBLECLICK : garde anti double-clic (le premier clic
    // lance deja la requete, un second avant la reponse ne doit rien faire).
    if (!arriving || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/lab/reception/arrivals`, {
        patient_id: arriving.patient_id, act_type: actType, has_appointment: hasApt,
      }, getAuthConfig());
      setArriving(null); setSearch(""); setResults([]);
      toast.success(`${arriving.first_name} ${arriving.last_name} ajouté à la file d'attente`);
      loadQueue();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };

  const act = async (q, action) => {
    // Correctif L-DOUBLECLICK : empeche un second clic sur la meme ligne
    // (ex: "Appeler" cliqué deux fois de suite) de partir une seconde fois
    // avant que le rafraichissement de la file n'ait eu lieu.
    if (busyIds.has(q.queue_id)) return;
    setBusyIds((prev) => new Set(prev).add(q.queue_id));
    try { await axios.put(`${API_URL}/api/lab/reception/queue/${q.queue_id}/${action}`, {}, getAuthConfig()); loadQueue(); }
    catch (e) { toast.error("Erreur"); }
    finally {
      setBusyIds((prev) => { const next = new Set(prev); next.delete(q.queue_id); return next; });
    }
  };

  const waitMinutes = (arrivedAt) => Math.max(0, Math.round((now - new Date(arrivedAt).getTime()) / 60000));

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Accueil</h1>

      {can("reception:manage") && (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, padding: 16, position: "relative" }}>
          <label style={lab}>Rechercher un patient (nom, NIN, téléphone...)</label>
          <input style={iS} value={search} onChange={(e) => doSearch(e.target.value)} placeholder="Ex: Dupont, 0555..." />
          {searching && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>Recherche...</div>}
          {results.length > 0 && (
            <div style={{ marginTop: 8, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
              {results.map((p) => (
                <div key={p.patient_id} onClick={() => openArrival(p)} style={{ padding: "10px 14px", borderTop: "1px solid #F1F5F9", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{p.first_name} {p.last_name}</div>
                    <div style={{ fontSize: 12, color: "#94A3B8" }}>{p.national_id ? p.national_id : ""}{p.phone ? ` · ${p.phone}` : ""}</div>
                  </div>
                  <button style={{ padding: "6px 14px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Patient arrivé</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Patient</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Arrivée</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Attente</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
          </tr></thead>
          <tbody>
            {queue.length === 0 && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun patient en attente.</td></tr>}
            {queue.map((q) => { const sc = RECEPTION_STATUS_COLORS[q.status] || ["#F1F5F9", "#64748B"]; return (
              <tr key={q.queue_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: "12px 14px", fontSize: 14 }}>
                  <div style={{ fontWeight: 500 }}>{q.patient_name || "—"}{q.priority && <span style={{ marginLeft: 6, fontSize: 11, color: "#A32D2D" }}>● Prioritaire</span>}</div>
                  {q.act_type && <div style={{ fontSize: 11, color: "#94A3B8" }}>{q.act_type}</div>}
                </td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{new Date(q.arrived_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{waitMinutes(q.arrived_at)} min</td>
                <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{RECEPTION_STATUS_LABELS[q.status] || q.status}</span></td>
                <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {can("reception:manage") && q.status === "attente" && <button onClick={() => act(q, "call")} disabled={busyIds.has(q.queue_id)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: busyIds.has(q.queue_id) ? "default" : "pointer", fontWeight: 600, opacity: busyIds.has(q.queue_id) ? 0.5 : 1 }}>Appeler</button>}
                  {can("reception:manage") && q.status === "appelee" && <button onClick={() => act(q, "start")} disabled={busyIds.has(q.queue_id)} style={{ fontSize: 13, color: "#3730A3", background: "none", border: "none", cursor: busyIds.has(q.queue_id) ? "default" : "pointer", fontWeight: 600, opacity: busyIds.has(q.queue_id) ? 0.5 : 1 }}>Commencer prise en charge</button>}
                  {can("reception:manage") && q.status === "en_cours" && <button onClick={() => act(q, "done")} disabled={busyIds.has(q.queue_id)} style={{ fontSize: 13, color: "#166534", background: "none", border: "none", cursor: busyIds.has(q.queue_id) ? "default" : "pointer", fontWeight: 600, opacity: busyIds.has(q.queue_id) ? 0.5 : 1 }}>Terminer prise en charge</button>}
                  {can("reception:manage") && <button onClick={() => act(q, "cancel")} disabled={busyIds.has(q.queue_id)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: busyIds.has(q.queue_id) ? "default" : "pointer", opacity: busyIds.has(q.queue_id) ? 0.5 : 1 }}>Annuler</button>}
                </span></td>
              </tr>); })}
          </tbody>
        </table>
      </div>

      {arriving && (
        <div onClick={() => setArriving(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 480, padding: 24, marginTop: 60 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>{arriving.first_name} {arriving.last_name}</h3>
            {arriving.national_id && <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 12px" }}>{arriving.national_id}</p>}
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16, fontSize: 12 }}>
              <span style={{ color: (arriving.first_name && arriving.last_name && arriving.date_of_birth) ? "#166534" : "#A32D2D" }}>
                {(arriving.first_name && arriving.last_name && arriving.date_of_birth) ? "✓" : "⚠"} Identité
              </span>
              <span style={{ color: arriving.phone ? "#166534" : "#A32D2D" }}>
                {arriving.phone ? "✓" : "⚠"} Coordonnées
              </span>
            </div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ marginBottom: 12 }}>
              <label style={lab}>Type d'acte (optionnel)</label>
              {actTypes.length > 0 ? (
                <select style={iS} value={actType} onChange={(e) => setActType(e.target.value)}>
                  <option value="">— Non précisé —</option>
                  {actTypes.map((a) => <option key={a.act_type_id} value={a.name}>{a.name} ({a.duration_minutes} min)</option>)}
                </select>
              ) : (
                <input style={iS} value={actType} onChange={(e) => setActType(e.target.value)} placeholder="Ex: Prise de sang, Bilan..." />
              )}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", marginBottom: 18, cursor: "pointer" }}>
              <input type="checkbox" checked={hasApt} onChange={(e) => setHasApt(e.target.checked)} /> Le patient avait un rendez-vous
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setArriving(null)} disabled={saving} style={{ padding: "9px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: saving ? "default" : "pointer" }}>Annuler</button>
              <button onClick={confirmArrival} disabled={saving} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}>{saving ? "Enregistrement..." : "Confirmer l'arrivée"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
